import React, { useCallback, useRef, useState } from 'react';
import { copyResultCard, resolveResultTitleKey } from '../share/resultShare.js';
import { GameConfetti } from './GameConfetti.jsx';

const COPY_STATUS = Object.freeze({
    IDLE: 'idle',
    COPYING: 'copying',
    COPIED_IMAGE: 'copied-image',
    COPIED_TEXT: 'copied-text',
    ERROR: 'error',
});

const COPY_LABEL_KEY_BY_STATUS = Object.freeze({
    [COPY_STATUS.COPYING]: 'result.copying',
    [COPY_STATUS.COPIED_IMAGE]: 'result.copiedImage',
    [COPY_STATUS.COPIED_TEXT]: 'result.copiedText',
    [COPY_STATUS.ERROR]: 'result.copyError',
});

/**
 * @param {{
 *   state: object,
 *   t: (key: string) => string,
 *   onPrimaryAction: () => void,
 *   primaryLabelKey?: string,
 * }} props
 */
export function GameResult({
    state,
    t,
    onPrimaryAction,
    primaryLabelKey = 'result.restart',
    showConfetti = false,
}) {
    const shareRef = useRef(null);
    const [copyStatus, setCopyStatus] = useState(COPY_STATUS.IDLE);
    const titleKey = resolveResultTitleKey(state);

    const goodTotal = state.stats.goodHits + state.stats.hotHits + (state.stats.activityHits || 0);

    const handleCopy = useCallback(async () => {
        if (!shareRef.current || copyStatus === COPY_STATUS.COPYING) return;

        setCopyStatus(COPY_STATUS.COPYING);

        try {
            const mode = await copyResultCard(shareRef.current, state, t);
            setCopyStatus(mode === 'image' ? COPY_STATUS.COPIED_IMAGE : COPY_STATUS.COPIED_TEXT);
            window.setTimeout(() => setCopyStatus(COPY_STATUS.IDLE), 2200);
        } catch {
            setCopyStatus(COPY_STATUS.ERROR);
            window.setTimeout(() => setCopyStatus(COPY_STATUS.IDLE), 2800);
        }
    }, [copyStatus, state, t]);

    const copyLabel = t(COPY_LABEL_KEY_BY_STATUS[copyStatus] ?? 'result.copy');

    return (
        <div className="lp-game__result" role="dialog" aria-modal="true">
            <GameConfetti active={showConfetti} />
            <div className="lp-game__result-card">
                <div ref={shareRef} className="lp-game__result-share">
                    <div className="lp-game__result-kicker">{t('game.title')}</div>
                    <h3>{t(titleKey)}</h3>

                    <div className="lp-game__result-score">
                        <span>{t('result.score')}</span>
                        <strong>{state.score}</strong>
                    </div>

                    <div className="lp-game__result-grid">
                        <div>
                            <span>{t('result.goodHits')}</span>
                            <strong>{goodTotal}</strong>
                        </div>
                        <div>
                            <span>{t('result.badHits')}</span>
                            <strong>{state.stats.badHits}</strong>
                        </div>
                    </div>
                </div>

                <div className="lp-game__result-actions">
                    <button
                        className="lp-game__result-button lp-game__result-button--secondary"
                        type="button"
                        onClick={handleCopy}
                        disabled={copyStatus === COPY_STATUS.COPYING}
                        aria-live="polite"
                    >
                        {copyLabel}
                    </button>
                    <button
                        className="lp-game__result-button"
                        type="button"
                        onClick={onPrimaryAction}
                    >
                        {t(primaryLabelKey)}
                    </button>
                </div>
            </div>
        </div>
    );
}
